'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Region } from '../types';

interface RegionCardProps {
  region: Region;
  priority?: boolean;
  className?: string;
}

// Derive up to 2 environment tags from region name + description
function getTags(region: Region): string[] {
  const text = `${region.name} ${region.short_desc || ''} ${region.desc || ''}`.toLowerCase();
  const tagMap: [string, string][] = [
    ['beach', 'Beach'], ['coast', 'Coastal'], ['island', 'Island'],
    ['mountain', 'Mountains'], ['alpine', 'Alpine'], ['high country', 'Alpine'],
    ['forest', 'Forest'], ['rainforest', 'Rainforest'],
    ['outback', 'Outback'], ['desert', 'Desert'],
    ['lake', 'Lakes'], ['river', 'Rivers'], ['gorge', 'Gorges'],
    ['national park', 'Nat. Park'], ['wilderness', 'Wilderness'],
    ['reef', 'Reef'], ['wetland', 'Wetlands'],
    ['wine', 'Wine Country'], ['surf', 'Surfing'],
  ];
  const found: string[] = [];
  for (const [kw, tag] of tagMap) {
    if (text.includes(kw)) { found.push(tag); }
    if (found.length >= 2) break;
  }
  return found;
}

const RegionCard = ({ region, priority = false, className = 'aspect-[4/3]' }: RegionCardProps) => {
  const href = `/apps/campsite-explorer/regions/${encodeURIComponent(region.name.toLowerCase().replace(/ /g, '-'))}`;
  const count = region.entity_count ?? region.entities?.length ?? 0;
  const tags = getTags(region);

  return (
    <Link
      href={href}
      className={`group block relative overflow-hidden rounded-xl bg-slate-900
        shadow-[0_1px_3px_rgba(0,0,0,0.1)]
        hover:shadow-[0_8px_28px_rgba(0,102,204,0.22)]
        focus:outline-none focus:ring-2 focus:ring-[#0066CC] focus:ring-offset-2
        transition-shadow duration-300 ${className}`}
    >
      <div className="relative w-full h-full overflow-hidden">
        {/* Image */}
        <Image
          src={region.image_src || '/default-region-image.jpg'}
          alt={`${region.name} camping region`}
          fill
          priority={priority}
          className="object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#003d7a]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400" />

        {/* Top row: state badge + count */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
          {region.state && (
            <span
              className="text-white text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider shadow-sm"
              style={{ background: 'linear-gradient(135deg, #0066CC, #0052A3)' }}
            >
              {region.state}
            </span>
          )}
          {count > 0 && (
            <span className="ml-auto bg-black/55 backdrop-blur-sm text-white/90 text-[11px] font-semibold px-2.5 py-1 rounded-md flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[#4da6ff] inline-block" />
              {count} sites
            </span>
          )}
        </div>

        {/* Bottom content */}
        <div className="absolute inset-x-0 bottom-0 p-4">
          {/* Environment tags */}
          {tags.length > 0 && (
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {tags.map(tag => (
                <span
                  key={tag}
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)' }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          <h3
            className="text-white font-bold text-[17px] leading-snug mb-1"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {region.name}
          </h3>

          {region.short_desc && (
            <p className="text-white/60 text-xs line-clamp-2 leading-relaxed group-hover:opacity-0 transition-opacity duration-200" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {region.short_desc}
            </p>
          )}
        </div>

        {/* Hover CTA */}
        <div className="absolute inset-x-4 bottom-4 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <div
            className="text-white text-sm font-semibold py-2.5 px-4 rounded-xl flex items-center justify-between shadow-xl"
            style={{ background: 'linear-gradient(135deg, #0066CC, #3872E5)', fontFamily: 'Work Sans, sans-serif' }}
          >
            <span>Explore Region</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        {/* Blue shimmer line on hover */}
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-[#0066CC] via-[#3872E5] to-[#0066CC] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
      </div>
    </Link>
  );
};

export default RegionCard;
